import { Flex, DatePicker, Table } from 'antd'
import dayjs from 'dayjs'
import { useState, Dispatch } from 'react'

import { useTreatmentServiceGetTreatmentPlanRecipeListEndpoint } from 'src/api/queries'
import { RecipeSortBy, RecipeStatus } from 'src/api/requests'
import { PageBreadcrumbs, Select } from 'src/components'
import {
  useRecipesConfig,
  useRecipesColumns
} from 'src/features/persons/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import {
  normalizeOrdering,
  getOrderingDirection,
  resetPagination
} from 'src/utils'

export const InstitutionRecipesPage = () => {
  const { t } = useLocales()
  const { userData } = useUser()

  const pagination = useTablePagination()

  const { options } = useRecipesConfig()
  const { columns } = useRecipesColumns(true)

  const [dateIssued, setDateIssued] = useState('')
  const [dateDispensed, setDateDispensed] = useState('')
  const [status, setStatus] = useState('')
  const [ordering, setOrdering] = useState('')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    legalEntityId: userData?.currentLegalEntityId,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as RecipeSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(dateIssued && {
      dateIssued
    }),
    ...(dateDispensed && {
      dispensingDate: dateDispensed
    }),
    ...(status && { status: status as RecipeStatus })
  }

  const { data, isLoading } =
    useTreatmentServiceGetTreatmentPlanRecipeListEndpoint(queryParams)

  const onFilterChange = (
    val: string | dayjs.Dayjs,
    setState: Dispatch<string>
  ) => {
    if (dayjs.isDayjs(val)) {
      setState(val.format(yearMonthDaySlash))
    } else {
      setState(val)
    }
    resetPagination(pagination)
  }

  const recipeStatusOptions = Object.entries(RecipeStatus).map(
    ([key, value]) => ({
      label: t(`recipes.status.${key}`.toLowerCase()),
      value
    })
  )

  return (
    <PageBreadcrumbs
      title={t('menu.prescriptions')}
      items={[{ title: t('menu.prescriptions') }]}
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          <DatePicker
            format={dayMonthYearBackSlash}
            className="full-width"
            placeholder={t('recipes.date_issued')}
            onChange={(val) => onFilterChange(val, setDateIssued)}
          />

          <DatePicker
            format={dayMonthYearBackSlash}
            className="full-width"
            placeholder={t('recipes.date_dispensing')}
            onChange={(val) => onFilterChange(val, setDateDispensed)}
          />

          <Select
            onSelect={(val) => onFilterChange(val, setStatus)}
            options={recipeStatusOptions}
          />

          <Select
            onSelect={(val) => onFilterChange(val, setOrdering)}
            options={options}
          />
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
